import { isNil } from '../../util/object';

export default class PriorityQueue {
  static INCREMENT_FUNCTION = {
    none(previous, increment) {
      return increment;
    },
    // Default increment function
    linear(previous, next) {
      return previous + next;
    },
    // Increments at a rate of 50%
    probabilistic_50(previous, increment) {
      const incrementBy = Math.round(Math.random() * increment);
      return previous + incrementBy;
    },
  };

  _keyPriority = null;
  _keyValue = null;

  /**
   * @function constructor
   * @param  {INCREMENT_FUNCTION} incrementFunction Function used to increment the priority counter for the priority queue keys
   * @return {PriorityQueue}
   */
  constructor(incrementFunction = PriorityQueue.INCREMENT_FUNCTION.none) {
    this._incrementFunction = incrementFunction;
    this._keyPriority = new Map();
    this._keyValue = new Map();
  }

  /**
   * @function enqueue Adds value to the queue with the specified priority.
   * @param  {?} key
   * @param  {Number} priorityIncrement How much to try to increment the queue by
   */
  enqueue(key, priorityIncrement = 1, value) {
    const currPriority = this._keyPriority.get(key); // undefined if none
    this._keyPriority.set(
      key,
      isNil(currPriority)
        ? priorityIncrement
        : this._incrementFunction(currPriority, priorityIncrement),
    );
    this._keyValue.set(key, value || null);
  }

  /**
   * @function changePriority Adjusts an existing value in the queue to have a new specified priority.
   * @param  {?} key
   * @param  {Number} newPriority
   */
  changePriority(key, newPriority) {
    this._keyPriority.set(key, newPriority);
  }

  /**
   * @function peek
   * @return { { key, priority } } The highest priority key / related priority in the queue, without removing it.
   */
  peek() {
    if (this.isEmpty()) return null;
    let highestPriority = 0;
    let highestPriorityKey = null;
    for (const [key, priority] of this._keyPriority) {
      // Ties are broken by the first enqueued key (See doc on Map)
      if (priority > highestPriority) {
        highestPriority = priority;
        highestPriorityKey = key;
      }

      if (highestPriorityKey === null) {
        highestPriorityKey = key;
      }
    }
    return {
      key: highestPriorityKey,
      priority: highestPriority,
      value: this._keyValue.get(highestPriorityKey),
    };
  }

  get(key) {
    return {
      key,
      priority: this._keyPriority.get(key),
      value: this._keyValue.get(key),
    };
  }

  /**
   * @function dequeue
   * @return {?} Removes and returns the highest priority key.
   */
  dequeue() {
    if (this.isEmpty()) return null;
    const {
      key: highestPriorityKey,
      value: highestPriorityValue,
    } = this.peek();
    this.delete(highestPriorityKey);
    return {
      key: highestPriorityKey,
      value: highestPriorityValue,
    };
  }

  /**
   * Does the queue have a key
   * @param key
   * @returns {boolean}
   */
  has(key) {
    return this._keyPriority.has(key);
  }

  /**
   * remove a key from the queue
   * @param key
   * @returns {*}
   */
  delete(key) {
    if (this.has(key)) {
      this._keyPriority.delete(key);
    }
    if (this._keyValue.has(key)) {
      this._keyValue.delete(key);
    }
  }

  /**
   * @function isEmpty
   * @return {Boolean} Returns true if the priority queue contains no elements.
   */
  isEmpty() {
    return this._keyPriority.size === 0;
  }

  /**
   * @function toString
   * @return {String} Converts the queue to a printable string representation.
   */
  toString() {
    const formattedValues = [];
    this._keyPriority.forEach((priority, key) => {
      formattedValues.push(
        `{ ${key}: { priority: ${priority} , value: ${this._keyValue.get(
          key,
        )}} }`,
      );
    });
    return formattedValues.reduce((pre, next) => {
      return `${pre}, ${next}`;
    });
  }

  /**
   * @function Removes all elements from the priority queue.
   */
  clear() {
    this._keyPriority.clear();
    this._keyValue.clear();
  }

  get length() {
    return this._keyPriority.size;
  }
}
